import { AbstractMouseEvent } from '../engine/AbstractMouseEvent';
import { VideoSceneObject } from '../engine/VideoSceneObject';
import { DebugConsole } from '../engine/DebugConsole';
import { CgsEvent } from '../engine/Event';
import { IConcreteResourceCache } from '../engine/IResourceCache';
import { Keyboard } from '../engine/Keyboard';
import { Matrix3 } from '../engine/Matrix3';
import { MouseEventTranslator } from '../engine/MouseEventTranslator';
import { Platform } from '../engine/Platform';
import { Rect } from '../engine/Rect';
import { SpriteBatch } from '../engine/SpriteBatch';
import { SpriteBatchCanvas } from '../engine/SpriteBatchCanvas';
import { SpriteBatchWebGL } from '../engine/SpriteBatchWebGL';
import { Vector2 } from '../engine/Vector2';
import { Application } from './Application';

export abstract class TemplateApplication extends Application {
  private _spriteBatch: SpriteBatch;

  public get spriteBatch(): SpriteBatch {
    return this._spriteBatch;
  }
  protected _mouseEventTranslator: MouseEventTranslator;
  protected _keyboard: Keyboard;
  public static scale: Vector2 = new Vector2(1, 1);

  private _coordinateSystem: Rect;

  public buildApp(
    platform: Platform,
    resourceCache: IConcreteResourceCache,
    coordinateSystem: Rect
  ) {
    this._coordinateSystem = coordinateSystem;

    super.buildApp(platform, resourceCache, coordinateSystem);

    this._mouseEventTranslator = new MouseEventTranslator(platform.inputSystem.mouse);
    this._keyboard = platform.inputSystem.keyboard;

    if (platform.videoSystem.isWebGL) {
      this._spriteBatch = new SpriteBatchWebGL(
        platform.view,
        platform.videoSystem.renderContext!,
        platform.videoSystem.renderDevice,
        this.renderSupport
      );
    } else {
      this._spriteBatch = new SpriteBatchCanvas(platform.view, this.renderSupport);
    }

    this.setCoordinateSystem(coordinateSystem);

    this.platform.view.sizeChanged.listen(() => {
      this.setCoordinateSystem(this._coordinateSystem);
    });
  }

  public initDeviceListeners(): void {
    this._mouseEventTranslator.subscribe((e) => this.dispatchEvent(e));
    this._keyboard.keyDown.listen((e) => this.dispatchEvent(e));
  }

  public setCoordinateSystem(rect: Rect): void {
    this._coordinateSystem = rect;
    this._spriteBatch.setCoordinateSystem(rect);

    const viewSize = new Vector2(this.platform.view.width, this.platform.view.height);
    const coordinateSystemSize = rect.size;
    const mouseTransform = Matrix3.fromScale(
      coordinateSystemSize.x / viewSize.x,
      coordinateSystemSize.y / viewSize.y
    ).multiply(Matrix3.fromTranslation(rect.lt.x, rect.lt.y));

    this._mouseEventTranslator.setTransform(mouseTransform);
  }

  public draw(): void {
    const spriteBatch = this._spriteBatch;

    spriteBatch.begin();
    this.drawImpl(spriteBatch);
    spriteBatch.end();

    const batchesCount = spriteBatch.batchesCount;
    const trianglesCount = spriteBatch.trianglesCount;

    DebugConsole.Print(`batches: ${batchesCount} triangles: ${trianglesCount}`);
  }

  public update(dt: number): void {
    this.updateImpl(dt);
  }

  public handleMouseEvents(events: AbstractMouseEvent[]): void {
    for (let i = 0; i < events.length; ++i) {
      this.dispatchEvent(events[i]);
    }
  }

  public abstract updateImpl(dt: number): void;

  public abstract drawImpl(spriteBatch: SpriteBatch): void;

  public abstract dispatchEvent(event?: CgsEvent): void;

  public contextLost(): void {
    this._spriteBatch.contextLost();
    this.resourceCache.contextLost();
    VideoSceneObject.contextLost();
  }

  public async contextReady(): Promise<void> {
    this._spriteBatch.contextReady();
    await this.resourceCache.contextReady();
  }
}
